import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import hr from "./hr";
import en from "./en";

const DICTS = { hr, en };
export const LANGUAGES = [
  { code: "hr", label: "Hrvatski", short: "HR" },
  { code: "en", label: "English", short: "EN" },
];
const STORAGE_KEY = "ll_lang";
const DEFAULT_LANG = "hr";

const I18nContext = createContext(null);

/**
 * Keeps <head> metadata in sync with the active UI language.
 * Croatian is the crawler-visible default (hard-coded in public/index.html);
 * this swaps title / description / OpenGraph / Twitter tags for EN visitors
 * and always keeps og:url + canonical pointing at the current origin.
 */
const setMeta = (selector, attr, value) => {
  const el = document.head.querySelector(selector);
  if (el) el.setAttribute(attr, value);
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const upsertMetaProperty = (property, content) => {
  let el = document.head.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

export const syncDocumentMeta = (lang) => {
  const dict = DICTS[lang] || DICTS[DEFAULT_LANG];
  const meta = dict.meta || DICTS[DEFAULT_LANG].meta;
  if (!meta) return;
  const alternate = lang === "en" ? "hr_HR" : "en_US";

  document.title = meta.title;
  setMeta('meta[name="description"]', "content", meta.description);
  upsertMetaProperty("og:title", meta.ogTitle);
  upsertMetaProperty("og:description", meta.ogDescription);
  upsertMetaProperty("og:image:alt", meta.imageAlt);
  upsertMetaProperty("og:locale", meta.locale);
  upsertMetaProperty("og:locale:alternate", alternate);
  setMeta('meta[name="twitter:title"]', "content", meta.ogTitle);
  setMeta('meta[name="twitter:description"]', "content", meta.ogDescription);
  setMeta('meta[name="twitter:image:alt"]', "content", meta.imageAlt);

  if (typeof window !== "undefined" && window.location) {
    const { origin, pathname } = window.location;
    upsertMetaProperty("og:url", `${origin}${pathname}`);
    upsertMetaProperty("og:image", `${origin}/og-ledgerlens.png`);
    setMeta('meta[name="twitter:image"]', "content", `${origin}/og-ledgerlens.png`);
    upsertLink("canonical", `${origin}${pathname}`);
  }
};

const lookup = (dict, path) =>
  path.split(".").reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), dict);

const interpolate = (text, vars) =>
  typeof text === "string" && vars
    ? text.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`))
    : text;

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(
    () => localStorage.getItem(STORAGE_KEY) || DEFAULT_LANG
  );

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
    syncDocumentMeta(lang);
  }, [lang]);

  const setLang = useCallback((next) => {
    const code = DICTS[next] ? next : DEFAULT_LANG;
    localStorage.setItem(STORAGE_KEY, code);
    setLangState(code);
  }, []);

  const t = useCallback(
    (path, vars) => {
      const value = lookup(DICTS[lang] || DICTS[DEFAULT_LANG], path);
      const fallback = value === undefined ? lookup(DICTS.en, path) : value;
      return interpolate(fallback === undefined ? path : fallback, vars);
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t, languages: LANGUAGES }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
};

export default I18nProvider;
